import { ProfileAvatar } from '@/components/shared/ProfileAvatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/Tooltip';
import { useI18n } from '@/context/I18nProvider';
import { formatPhoneNumber } from '@/lib/utils';
import { IChannel } from '@/services/types';

type ConversationItemProps = {
  id: string;
  channel: IChannel;
  name: string;
  date: string;
  message: string;
  isActive: boolean;
  onClick: () => void;
  profilePictureURL?: string;
  businessWhatsappPhoneNumber?: string;
  businessInstagramUsername?: string;
  isMobile?: boolean;
};

const ConversationItem = ({
  id,
  channel,
  name,
  message,
  isActive,
  onClick,
  profilePictureURL,
  businessWhatsappPhoneNumber,
  businessInstagramUsername,
  isMobile,
}: ConversationItemProps) => {
  const { t } = useI18n();
  const contactedFrom = formatPhoneNumber(businessWhatsappPhoneNumber) || businessInstagramUsername;

  // ------------------ RENDER ---------------------------------------

  return (
    <div
      onClick={onClick}
      className={`flex overflow-hidden rounded-lg cursor-pointer transition-colors
        ${isMobile ? 'gap-2.5 p-2.5 pr-3' : 'gap-3 p-3 pr-5'}
        ${isActive ? 'bg-muted' : 'hover:bg-muted/50'}
      `}
    >
      <ProfileAvatar
        profilePictureURL={profilePictureURL}
        channel={channel}
        displayName={name}
        leadID={id}
        size={isMobile ? 'xl' : 'lg'}
        className="self-center shrink-0"
      />
      <div className="flex flex-1 flex-col gap-0.5 min-w-0">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <span
            className={`truncate font-medium leading-[21px] min-w-0 ${
              isMobile ? 'text-[13px]' : 'text-sm'
            } ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}
            title={name}
          >
            {name}
          </span>

          {contactedFrom ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={`text-xs font-medium leading-[21px] truncate max-w-[40%] ${
                      isActive ? 'text-foreground' : 'text-muted-foreground'
                    }`}
                    title={contactedFrom}
                  >
                    {contactedFrom}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="bg-white text-black px-2 py-1 border-[#F3F4F6] border shadow">
                  {t('common.contactedFrom')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
        </div>
        <p className="text-xs font-normal leading-[18px] text-muted-foreground line-clamp-1">
          {message}
        </p>
      </div>
    </div>
  );
};

export { ConversationItem };
